/**
 * The denylist spec — the single source of truth for what "payload data"
 * means across every Reflecto surface (server, extension, web; the deferred
 * Android Crashlytics hardening will reference it too).
 *
 * Consumed by ./scrub.ts (the shared scrubber engine) and by
 * apps/server/src/redactLog.ts (public-API token log redaction). Adding an
 * entry here hardens every surface at once; every addition needs a matching
 * test vector in packages/core/src/scrub/__tests__/scrub.test.ts. See
 * docs/observability.md ("Adding a new scrub rule").
 *
 * Kept as plain TypeScript rather than a JSON asset deliberately: this
 * package's own tsc build has no resolveJsonModule and does not copy JSON
 * assets to dist/, and a typed const gives the same one-file
 * source-of-truth property with none of the loader/emit risk.
 */

export const REDACTED = "[scrubbed]";

/**
 * Field-name tokens whose values must never reach an observability vendor.
 * Key names are tokenized on camelCase / snake_case / kebab-case / dot /
 * acronym boundaries and matched per-token with a naive singularizer, so
 * `pubKey`, `fcm_token`, `pairingToken`, `queueKey`, `APIKey`, `cookies`,
 * `bigText`, and `conversationTitle` all match.
 *
 * First block: the Stage 0 server set. Second block: client-side additions —
 * the extension and web app hold DECRYPTED notification content, and these
 * are the field names that content travels under (DecryptedNotification,
 * ConversationMessage, clip stores).
 */
export const DENY_KEY_TOKENS: ReadonlySet<string> = new Set([
  // Stage 0 (server) set
  "blob",
  "body",
  "payload",
  "message",
  "content",
  "title",
  "ciphertext",
  "plaintext",
  "nonce",
  "key",
  "token",
  "authorization",
  "cookie",
  "secret",
  "password",
  "subscription",
  // Stage 1 client-side additions (decrypted-notification field names)
  "text",
  "subtext",
  "bigtext",
  "sender",
  "conversation",
  "clip",
]);

/**
 * OTel/Sentry span-attribute names that can smuggle Redis keys (which embed
 * device IDs) or SQL. Dotted names don't tokenize usefully — matched exactly.
 */
export const DENY_EXACT_KEYS: ReadonlySet<string> = new Set([
  "db.statement",
  "db.query.text",
  "cache.key",
  "cache.keys",
]);

/** Keys whose string values are URLs: query strings are stripped entirely. */
export const URL_KEY_TOKENS: ReadonlySet<string> = new Set(["url", "uri", "href"]);

/**
 * Public-API token shapes (`rfk_live_…`, `rfd_test_…`). Single-homed here;
 * apps/server/src/redactLog.ts consumes it for access-log redaction and the
 * scrubber engine applies it to free text. The `rfd_` prefix is
 * forward-looking (receiver tokens, V1 spec §5.1).
 */
export const PUBLIC_TOKEN_PATTERN = /(rfk|rfd)_(live|test)_[A-Za-z0-9]{32}/g;

/** JWT-shaped strings (`eyJ` header, dot-separated segments; sig optional). */
export const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;

/** Credentials in URL authority parts: redis://user:pass@host, https://x:y@z. */
export const URL_USERINFO_PATTERN = /\b([a-z][a-z0-9+.-]*:\/\/)[^/\s@]+@/gi;

/**
 * Long unbroken base64/base64url runs — ciphertext envelopes, keys, tokens.
 * 64 chars sits comfortably above opaque IDs (msgId/deviceId) so diagnostics
 * survive, while real encrypted blobs and key material run far longer.
 * (Known accepted gap: sub-64-char base64 in free text — see
 * docs/observability.md.)
 */
export const BASE64_PATTERN = /[A-Za-z0-9+/_-]{64,}={0,2}/g;
